/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteMcpProbeURL } from "../funcs/remoteMcpProbeURL.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ProbeURLResult } from "../models/components/probeurlresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ProbeRemoteMcpURLRequest,
  ProbeRemoteMcpURLSecurity,
} from "../models/operations/proberemotemcpurl.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ProbeRemoteMcpURLMutationVariables = {
  request: ProbeRemoteMcpURLRequest;
  security?: ProbeRemoteMcpURLSecurity | undefined;
  options?: RequestOptions;
};

export type ProbeRemoteMcpURLMutationData = ProbeURLResult;

export type ProbeRemoteMcpURLMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * probeURL remoteMcp
 *
 * @remarks
 * Probe a candidate remote MCP server URL by issuing an MCP initialize request and reporting whether MCP is available, authentication is required, the response is invalid, or the server is unreachable.
 */
export function useProbeRemoteMcpURLMutation(
  options?: MutationHookOptions<
    ProbeRemoteMcpURLMutationData,
    ProbeRemoteMcpURLMutationError,
    ProbeRemoteMcpURLMutationVariables
  >,
): UseMutationResult<
  ProbeRemoteMcpURLMutationData,
  ProbeRemoteMcpURLMutationError,
  ProbeRemoteMcpURLMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildProbeRemoteMcpURLMutation(client, options),
    ...options,
  });
}

export function mutationKeyProbeRemoteMcpURL(): MutationKey {
  return ["@gram/client", "remoteMcp", "probeURL"];
}

export function buildProbeRemoteMcpURLMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ProbeRemoteMcpURLMutationVariables,
  ) => Promise<ProbeRemoteMcpURLMutationData>;
} {
  return {
    mutationKey: mutationKeyProbeRemoteMcpURL(),
    mutationFn: function probeRemoteMcpURLMutationFn({
      request,
      security,
      options,
    }): Promise<ProbeRemoteMcpURLMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteMcpProbeURL(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
